import {
  OptionSourceSystemCoreDataTypeSchema,
  type OptionSourceSystemCoreDataType,
} from '@omnea-app/packages/universal/core-data/systemCoreDataType'
import { UnionToIntersection } from '@omnea-app/packages/universal/utility-types/utilityTypes'

export type CamelEntityType = (typeof typeTranslations)[number]['camel'] // TODO: Rename this when the migration of types casing is over
export type CamelSystemCoreDataType = (typeof systemCoreDataTranslations)[number]['camel'] // TODO: Rename this when the migration of types casing is over
export type KebabSystemCoreDataType = (typeof systemCoreDataTranslations)[number]['kebab']
export type CamelOptionSourceSystemCoreDataType = OptionSourceSystemCoreDataTranslation['camel']
export type OptionSourceDynamicOptionSystemSource = OptionSourceSystemCoreDataTranslation['dynamicOptionSource']
export type AnyDependantEntityType = keyof SystemCoreDataTranslationObject // Excludes supplier-profile (things that CAN have dependencies)
export type AnyDependencyEntityType = keyof EntityTypeTranslationObject // Includes supplier-profile (things that can BE dependencies)
export const isAnyDependantEntityType = (type: unknown): type is AnyDependantEntityType =>
  typeof type === 'string' && type in systemCoreDataTranslationsObject
export const isAnyDependencyEntityType = (type: unknown): type is AnyDependencyEntityType =>
  typeof type === 'string' && type in typeTranslationsObject
export const isAnySystemCoreDataType = (type: unknown): type is CamelSystemCoreDataType =>
  typeof type === 'string' && type in systemCoreDataTranslationsObject
export const isKebabSystemCoreDataType = (type: unknown): type is KebabSystemCoreDataType =>
  typeof type === 'string' && systemCoreDataTranslations.some((t) => t.kebab === type)
export const isOptionSourceSystemCoreDataType = (type: unknown): type is keyof OptionSourceSystemCoreDataTranslationObject =>
  typeof type === 'string' && Object.hasOwn(optionSourceSystemCoreDataTranslationsObject, type)
/**
 * Given an entity type in any format, translate it to the desired format.
 */
export const formatEntityType = <Type extends keyof EntityTypeTranslationObject, Format extends keyof EntityTypeObject>(
  type: Type,
  to: Format
): EntityTypeTranslationObject[Type][Format] => {
  // Throw a descriptive error for any mistypings/castings of "type"
  if (!(type in typeTranslationsObject)) {
    throw new Error(`Unknown entity type: "${type}" (JS type: ${typeof type})`)
  }

  return typeTranslationsObject[type][to]
}

const systemCoreDataTranslations = [
  // Address
  {
    dynamicOptionSource: 'addresses',
    camel: 'address',
    camelPlural: 'addresses',
    kebab: 'address',
    snake: 'address',
    snakePlural: 'addresses',
  },

  // Currency
  {
    dynamicOptionSource: 'currencies',
    camel: 'currency',
    camelPlural: 'currencies',
    kebab: 'currency',
    snake: 'currency',
    snakePlural: 'currencies',
  },

  // Department
  {
    dynamicOptionSource: 'department', // The only one that is not plural (╯°□°)╯︵ ┻━┻
    camel: 'department',
    camelPlural: 'departments',
    kebab: 'department',
    snake: 'department',
    snakePlural: 'departments',
  },

  // Line item type
  {
    dynamicOptionSource: 'line-item-types',
    camel: 'lineItemType',
    camelPlural: 'lineItemTypes',
    kebab: 'line-item-type',
    snake: 'line_item_type',
    snakePlural: 'line_item_types',
  },

  // Payment method
  {
    dynamicOptionSource: 'payment-methods',
    camel: 'paymentMethod',
    camelPlural: 'paymentMethods',
    kebab: 'payment-method',
    snake: 'payment_method',
    snakePlural: 'payment_methods',
  },

  // Payment term
  {
    dynamicOptionSource: 'payment-terms',
    camel: 'paymentTerm',
    camelPlural: 'paymentTerms',
    kebab: 'payment-term',
    snake: 'payment_term',
    snakePlural: 'payment_terms',
  },

  // Tax type
  {
    dynamicOptionSource: 'tax-types',
    camel: 'taxType',
    camelPlural: 'taxTypes',
    kebab: 'tax-type',
    snake: 'tax_type',
    snakePlural: 'tax_types',
  },

  // Subsidiary
  {
    dynamicOptionSource: 'subsidiaries',
    camel: 'subsidiary',
    camelPlural: 'subsidiaries',
    kebab: 'subsidiary',
    snake: 'subsidiary',
    snakePlural: 'subsidiaries',
  },

  // Accounting period
  {
    dynamicOptionSource: 'accounting-periods',
    camel: 'accountingPeriod',
    camelPlural: 'accountingPeriods',
    kebab: 'accounting-period',
    snake: 'accounting_period',
    snakePlural: 'accounting_periods',
  },
] as const satisfies EntityTypeObject[]

export type DynamicOptionSystemSource = (typeof systemCoreDataTranslations)[number]['dynamicOptionSource']
export const DYNAMIC_OPTION_SYSTEM_SOURCES: readonly DynamicOptionSystemSource[] = systemCoreDataTranslations.map(
  (translation) => translation.dynamicOptionSource
)

// Not `isSystemDynamicOptionSource`, which omits accounting periods.
export const isDynamicOptionSystemSource = (value: string): value is DynamicOptionSystemSource =>
  DYNAMIC_OPTION_SYSTEM_SOURCES.some((source) => source === value)

const typeTranslations = [
  ...systemCoreDataTranslations,

  // Supplier profile
  {
    dynamicOptionSource: 'supplier-profiles',
    camel: 'supplierProfile',
    camelPlural: 'supplierProfiles',
    kebab: 'supplier-profile',
    snake: 'supplier_profile',
    snakePlural: 'supplier_profiles',
  },
] as const satisfies EntityTypeObject[]

// 👇 HELPERS

interface EntityTypeObject {
  dynamicOptionSource: string
  camel: string
  camelPlural: string
  kebab: string
  snake: string
  snakePlural: string
}

type SingleMap<T extends EntityTypeObject> = T[keyof T] extends `${infer Value}` ? Record<Value, T> : Record<never, never>

const buildSingleMap = <T extends EntityTypeObject>(object: T): SingleMap<T> => {
  return Object.values(object).reduce(
    (acc, value) => {
      acc[value] = object
      return acc
    },
    {} as Record<string, T>
  ) as SingleMap<T>
}

type Map<T extends EntityTypeObject> = UnionToIntersection<T extends EntityTypeObject ? SingleMap<T> : Record<never, never>>

// No prototype, because most lookups into these maps test membership with `in`: on a plain object
// `'constructor' in map` is true and the translation resolves to undefined.
const buildMap = <T extends EntityTypeObject>(mappingObjects: readonly T[]): Map<T> => {
  return Object.assign(Object.create(null), ...mappingObjects.map(buildSingleMap))
}

type OptionSourceSystemCoreDataTranslation = Extract<
  (typeof systemCoreDataTranslations)[number],
  { kebab: OptionSourceSystemCoreDataType }
>

const isOptionSourceTranslation = (
  translation: (typeof systemCoreDataTranslations)[number]
): translation is OptionSourceSystemCoreDataTranslation =>
  OptionSourceSystemCoreDataTypeSchema.safeParse(translation.kebab).success

const typeTranslationsObject = buildMap(typeTranslations)
const systemCoreDataTranslationsObject = buildMap(systemCoreDataTranslations)
const optionSourceSystemCoreDataTranslationsObject = buildMap(systemCoreDataTranslations.filter(isOptionSourceTranslation))
type EntityTypeTranslationObject = typeof typeTranslationsObject
type SystemCoreDataTranslationObject = typeof systemCoreDataTranslationsObject
type OptionSourceSystemCoreDataTranslationObject = typeof optionSourceSystemCoreDataTranslationsObject

export const isSystemCoreDataType = (type: string): type is keyof SystemCoreDataTranslationObject => {
  return type in systemCoreDataTranslationsObject
}

/** Normalizes system core data ids and dynamic-option `dataSource` values to the form dataSource string. */
export const toDynamicOptionDataSource = (source: string): string => {
  if (isAnySystemCoreDataType(source)) {
    return formatEntityType(source, 'dynamicOptionSource')
  }
  return source
}

export const entitySourceMatchesDynamicOptionDataSource = (entitySource: string, questionDataSource: string): boolean =>
  toDynamicOptionDataSource(entitySource) === toDynamicOptionDataSource(questionDataSource)
